export const mapElementsToIndex = <
  ElementType extends HTMLElement = HTMLLIElement,
>(
  elements: ReadonlyArray<ElementType> | null,
): WeakMap<ElementType, number> => {
  const map = new WeakMap();

  if (elements) {
    elements.forEach((el, ix) => {
      map.set(el, ix);
    });
  }

  return map;
};

export const createClickHandler =
  <ElementType extends HTMLElement = HTMLLIElement>(
    showModal: boolean,
    modalTriggeringElement: ElementType | null,
    setModalTriggeringElement: (arg1: ElementType) => void,
    setModalOpen: (arg1: boolean) => void,
    setOpenIndex: (ix: number | null) => void,
  ) =>
  (evt: UIEvent) => {
    if (!showModal || modalTriggeringElement) {
      return;
    }
    const target = evt.currentTarget as ElementType;
    setModalTriggeringElement(target);
    setModalOpen(true);
    setOpenIndex(null);
    evt.preventDefault();
  };

type EventHandler = (arg1: UIEvent) => void;

export const setListenersOnElements = <
  ElementType extends HTMLElement = HTMLLIElement,
>(
  elements: Array<ElementType>,
  showModal: boolean,
  handleMouseEnter: EventHandler,
  handleMouseLeave: EventHandler,
  handleClick: EventHandler,
) => {
  elements.forEach(element => {
    if (showModal) {
      element.addEventListener('click', handleClick, true);
      return;
    }

    element.addEventListener('mouseenter', handleMouseEnter);
    element.addEventListener('focus', handleMouseEnter);
    element.addEventListener('mouseleave', handleMouseLeave);
    element.addEventListener('blur', handleMouseLeave);
  });
};

export const removeListenersOnElements = <
  ElementType extends HTMLElement = HTMLLIElement,
>(
  elements: Array<ElementType>,
  showModal: boolean,
  handleMouseEnter: EventHandler,
  handleMouseLeave: EventHandler,
  handleClick: EventHandler,
) => {
  elements.forEach(element => {
    if (showModal) {
      element.removeEventListener('click', handleClick, true);
      return;
    }

    element.removeEventListener('mouseenter', handleMouseEnter);
    element.removeEventListener('focus', handleMouseEnter);
    element.removeEventListener('mouseleave', handleMouseLeave);
    element.removeEventListener('blur', handleMouseLeave);
  });
};

export const createMouseEnterHandler =
  <ElementType extends HTMLElement = HTMLLIElement>(
    elMap: WeakMap<ElementType, number>,
    setOpenIndex: (ix: number | null) => void,
  ) =>
  (evt: UIEvent) => {
    const target = evt.currentTarget as ElementType;
    const index = elMap.get(target);
    const openIndex = typeof index === 'undefined' ? null : index;
    setOpenIndex(openIndex);
  };
